/**
 * Admission payments API (EPIC C / C4) — 1:1 with the frozen §1 API CONTRACT
 * (state/epics/C/C4/build-plan.md). Routes mount under the gated IPD router
 * (`/api/organisation`). ALL money on the wire is INTEGER PAISE — no rupee
 * float ever crosses. Deposit collect + refund are money writes, so both carry
 * a REQUIRED `X-Idempotency-Key` header (a fresh key per dialog open → a double
 * click / retry replays to ONE row, never two).
 *
 * Errors = `detail:{code, ...fields}`; the caller keys off `code` (machine
 * code), never message text — e.g. REFUND_EXCEEDS_REMAINING carries a
 * `remaining_paise` so the dialog can show the refreshed cap inline.
 *
 * Each function returns `{ success, data?, error?, code?, ...extras }`.
 */

import apiClient from './apiClient';
import { API_CONFIG } from '../../constants';

const BASE = '/api/organisation/admissions';
const enc = encodeURIComponent;

/** A fresh idempotency key per money-write attempt (dialog open). */
export function newIdempotencyKey(prefix = 'ipd') {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return `${prefix}:${crypto.randomUUID()}`;
  return `${prefix}:${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** IPD error-shape translation (code + extras like remaining_paise pass through). */
function parseError(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.code) {
    const { code, message, ...extras } = detail;
    return { error: message || fallback, code, ...extras };
  }
  if (typeof detail === 'string') return { error: detail };
  switch (error?.response?.status) {
    case 401: return { error: 'Session expired. Please login again.' };
    case 403: return { error: 'You do not have permission for this action.' };
    case 404: return { error: 'Admission or payment not found.' };
    case 409: return { error: 'That action conflicts with the current state.', code: 'CONFLICT' };
    case 422: return { error: fallback, code: 'VALIDATION_ERROR' };
    case 503: return { error: 'Admissions are temporarily unavailable. Try again shortly.', code: 'IPD_TABLE_UNAVAILABLE' };
    default:  return { error: fallback };
  }
}

/**
 * C-2 POST /admissions/{id}/deposits — collect an advance deposit. The SERVER
 * prices/validates; the body carries only integer-paise amount + mode +
 * optional reference/notes (never a client-computed balance → 422 on extras).
 * A 201 (created) and a 200 (idempotent replay) are BOTH success; a replay
 * surfaces `idempotent_replay: true`.
 *
 * C5 §10 (additive): optional `expected_balance_paise` — the settle intent.
 * When present the server conditions the admission leg on that exact
 * balance; a lost race returns 409 BALANCE_CHANGED with the fresh balance.
 * Window widens from ADMITTED-only to `SETTLEMENT_WINDOW_STATUSES`.
 *
 * Envelope: { payment, balance_paise_after, deposit_total_paise_after, deposit_low }
 */
export const collectDeposit = async (admissionId, { amount_paise, method, reference_number, notes, idempotency_key, expected_balance_paise } = {}) => {
  try {
    const body = { amount_paise, method };
    if (reference_number) body.reference_number = reference_number;
    if (notes) body.notes = notes;
    if (expected_balance_paise !== undefined) body.expected_balance_paise = expected_balance_paise;
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/deposits`, body, {
      headers: { 'X-Idempotency-Key': idempotency_key },
    });
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.error('[AdmissionPaymentsAPI] collectDeposit error:', error);
    return { success: false, ...parseError(error, 'Could not collect the deposit.') };
  }
};

/**
 * C-3 POST /admissions/{id}/payments/{paymentId}/refund — refund part/all of a
 * deposit. Server enforces the NET cap; an over-ask returns 400
 * REFUND_EXCEEDS_REMAINING + `remaining_paise` (the refreshed cap) so the loser
 * of a race sees the true remaining rather than a silent success.
 *
 * C5 §11 (additive): optional `expected_balance_paise` (negative balance —
 * the settle intent); server caps at min(NET remaining, |expected|). Window
 * widens to `SETTLEMENT_WINDOW_STATUSES`; REFUND_DEPOSIT stays ADMIN-only
 * (owner ruling 1 — not relaxed).
 *
 * Owner redesign: `method` accepts any of CASH/UPI/CARD (any-in → any-out) and
 * `reference` is REQUIRED when method ∈ {UPI,CARD} (server 422 REFERENCE_REQUIRED);
 * omitted/null for CASH.
 *
 * Envelope: { refund, parent, balance_paise_after, deposit_low }
 */
export const refundDeposit = async (admissionId, paymentId, { amount_paise, reason, method, reference, idempotency_key, expected_balance_paise } = {}) => {
  try {
    const body = { amount_paise, reason };
    if (method) body.method = method;
    if (reference) body.reference = reference;
    if (expected_balance_paise !== undefined) body.expected_balance_paise = expected_balance_paise;
    const response = await apiClient.post(
      `${BASE}/${enc(admissionId)}/payments/${enc(paymentId)}/refund`,
      body,
      { headers: { 'X-Idempotency-Key': idempotency_key } },
    );
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.error('[AdmissionPaymentsAPI] refundDeposit error:', error);
    return { success: false, ...parseError(error, 'Could not record the refund.') };
  }
};

/**
 * Owner redesign — POST /admissions/{id}/payments/{parentPaymentId}/return-advance
 * (perm RETURN_ADVANCE; reception + admin). The BOUNDED discharge-time return of
 * the definite pending excess: the SERVER sets the amount (= the excess) and the
 * reason — the client sends ONLY the payout `method` (CASH default) + `reference`
 * (REQUIRED for UPI/CARD → 422 REFERENCE_REQUIRED). Money write → REQUIRED
 * `X-Idempotency-Key` (a double-click / retry replays to ONE voucher).
 *
 * Errors: 422 REFERENCE_REQUIRED, 409 NO_EXCESS_TO_RETURN, 409 ADMISSION_NOT_ACTIVE.
 * Returns the same RefundResponse envelope as `refundDeposit`:
 *   { refund, parent, balance_paise_after, deposit_low }
 */
export const returnAdvance = async (admissionId, parentPaymentId, { method, reference, idempotency_key } = {}) => {
  try {
    const body = { method };
    if (reference) body.reference = reference;
    const response = await apiClient.post(
      `${BASE}/${enc(admissionId)}/payments/${enc(parentPaymentId)}/return-advance`,
      body,
      { headers: { 'X-Idempotency-Key': idempotency_key } },
    );
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.error('[AdmissionPaymentsAPI] returnAdvance error:', error);
    return { success: false, ...parseError(error, 'Could not return the advance.') };
  }
};

/**
 * C-7 GET /admissions/{id}/payments/{paymentId}/receipt — deposit receipt or
 * refund voucher PDF. Reprint = pure re-render (never a re-write). Opens in the
 * in-app FileViewerStore (no native window). `source_type=REFUND` renders the
 * refund voucher.
 */
export const openAdmissionReceiptPdf = async (admissionId, paymentId, { isRefund = false } = {}) => {
  try {
    const response = await apiClient.get(
      `${BASE}/${enc(admissionId)}/payments/${enc(paymentId)}/receipt`,
      { responseType: 'blob' },
    );
    const blob = new Blob([response.data], { type: 'application/pdf' });
    const blobUrl = URL.createObjectURL(blob);
    const useFileViewerStore = (await import('../../stores/useFileViewerStore')).default;
    useFileViewerStore.getState().openViewer({
      blobUrl,
      fileType: 'pdf',
      title: isRefund ? 'Refund voucher' : 'Deposit receipt',
    });
    return { success: true, blobUrl };
  } catch (error) {
    console.error('[AdmissionPaymentsAPI] openAdmissionReceiptPdf error:', error);
    return { success: false, ...parseError(error, 'Could not open the receipt.') };
  }
};

// ═══════════════════════════════════════════════════════════════════════
// Dues collection (C6b — mirror of the frozen API CONTRACT,
// state/epics/C/C6b/build-plan.md). Collect is a money write → REQUIRED
// `X-Idempotency-Key`; the queue read carries none; write-off carries none
// either (the server's singleton record row is its own idempotency guard —
// R-3, no client key needed or accepted).
// ═══════════════════════════════════════════════════════════════════════

/**
 * §1 POST /admissions/{id}/dues/collect — collect part/all of the open dues
 * left on a discharged (LAMA/DAMA/deceased) admission. `expected_dues_paise`
 * is the CAS figure the caller saw; a lost race returns 409 DUES_CHANGED with
 * the fresh `dues_paise`. Over-asking returns 422 AMOUNT_EXCEEDS_DUES with the
 * true `dues_paise`. A replay of the same key returns the ORIGINAL receipt
 * with `idempotent_replay: true` (never a second receipt).
 *
 * Envelope: { payment, dues_paise_after, dues_collected_paise_after,
 *             dues_status_after, balance_paise_after, idempotent_replay }
 */
export const collectDues = async (admissionId, {
  amount_paise,
  method,
  reference_number,
  received_from,
  notes,
  expected_dues_paise,
  idempotency_key,
} = {}) => {
  try {
    const body = { amount_paise, method, expected_dues_paise };
    if (reference_number) body.reference_number = reference_number;
    if (received_from) body.received_from = received_from;
    if (notes) body.notes = notes;
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/dues/collect`, body, {
      headers: { 'X-Idempotency-Key': idempotency_key },
    });
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.error('[AdmissionPaymentsAPI] collectDues error:', error);
    return { success: false, ...parseError(error, 'Could not collect the dues.') };
  }
};

/**
 * §2 GET /admissions/dues/pending — the Payment Pending queue. No params v1
 * (bounded + self-pruning; sort/search/aging apply client-side). Rows arrive
 * oldest-first and are server-enriched with patient_name/ip_number.
 *
 * Envelope: { items: DuesPendingRow[], count, total_dues_paise }
 */
export const fetchDuesPending = async () => {
  try {
    const response = await apiClient.get(`${BASE}/dues/pending`);
    return { success: true, data: response.data };
  } catch (error) {
    console.error('[AdmissionPaymentsAPI] fetchDuesPending error:', error);
    return { success: false, ...parseError(error, 'Could not load the Payment Pending queue.') };
  }
};

/**
 * §3 POST /admissions/{id}/dues/write-off — admin-only remainder write-off.
 * NO idempotency header (the singleton write-off record row is the guard);
 * a repeat call returns the ORIGINAL record with `idempotent_replay: true`.
 * `expected_dues_paise` is the CAS figure; a lost race (a collect won)
 * returns 409 DUES_CHANGED with the fresh `dues_paise`.
 *
 * Envelope: { writeoff, dues_status_after, dues_paise_after,
 *             dues_collected_paise, balance_paise_after, idempotent_replay }
 */
export const writeOffDues = async (admissionId, { reason, expected_dues_paise } = {}) => {
  try {
    const body = { reason, expected_dues_paise };
    const response = await apiClient.post(`${BASE}/${enc(admissionId)}/dues/write-off`, body);
    return { success: true, data: response.data, status: response.status };
  } catch (error) {
    console.error('[AdmissionPaymentsAPI] writeOffDues error:', error);
    return { success: false, ...parseError(error, 'Could not write off the dues.') };
  }
};

/** §4 GET /admissions/{id}/dues/write-off/voucher — absolute URL for print/download. */
export const writeOffVoucherUrl = (admissionId) =>
  `${API_CONFIG.BACKEND_URL}${BASE}/${enc(admissionId)}/dues/write-off/voucher`;

const admissionPaymentsApi = {
  collectDeposit,
  refundDeposit,
  returnAdvance,
  openAdmissionReceiptPdf,
  newIdempotencyKey,
  collectDues,
  fetchDuesPending,
  writeOffDues,
  writeOffVoucherUrl,
};

export default admissionPaymentsApi;
